import { useFormik } from 'formik';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import LoadingButton from '@shared/components/buttons/LoadingButton/LoadingButton';
import type { CustomerInfoFormValues } from '@t/checkout.types';
import { buildCustomerInfoSchema } from '@components/Checkout/steps/CustomerInfoStep/utils/customerInfoStepSchema';
import { INITIAL_VALUES } from '@components/Checkout/steps/CustomerInfoStep/utils/customerInfoStepUtils';
import { PersonalDetailsSection } from '@components/Checkout/steps/CustomerInfoStep/sections/PersonalDetailsSection';
import { ContactInfoSection } from '@components/Checkout/steps/CustomerInfoStep/sections/ContactInfoSection';
import { SecuritySection } from '@components/Checkout/steps/CustomerInfoStep/sections/SecuritySection';
import { ServiceAddressDisplay } from '@components/Checkout/steps/CustomerInfoStep/sections/ServiceAddressDisplay';
import { BillingAddressSection } from '@components/Checkout/steps/CustomerInfoStep/sections/BillingAddressSection';
import { BillDeliverySection } from '@components/Checkout/steps/CustomerInfoStep/sections/BillDeliverySection';
import { useTenantConfigContext } from '@providers/TenantConfigProvider';
import type { ActiveFormProps } from '@components/Checkout/steps/CustomerInfoStep/CustomerInfoStep.types';

const DIVIDER_SX = { height: '1.5px' };

export const ActiveForm = ({
  metadata,
  serviceAddress,
  savedValues,
  isSaving,
  onSave,
  onEditServiceAddress,
}: ActiveFormProps) => {
  const { tmfEnabled, isLoading: isTenantConfigLoading } = useTenantConfigContext();

  const formik = useFormik<CustomerInfoFormValues>({
    initialValues: savedValues ?? INITIAL_VALUES,
    validationSchema: buildCustomerInfoSchema(metadata?.passwordRequirements, tmfEnabled),
    enableReinitialize: true,
    onSubmit: onSave,
  });

  const titleOptions = (metadata?.titles ?? []).map((t) => ({ label: t.name, value: String(t.titleId) }));
  const phoneTypeOptions = (metadata?.phoneNumberTypes ?? []).map((t) => ({
    label: t.name,
    value: String(t.phoneNumberTypeId),
  }));

  return (
    <form onSubmit={formik.handleSubmit}>
      <Stack gap="30px">
        <PersonalDetailsSection formik={formik} titleOptions={titleOptions} />

        <Divider sx={DIVIDER_SX} />

        <ContactInfoSection formik={formik} phoneTypeOptions={phoneTypeOptions} />

        {!tmfEnabled && !isTenantConfigLoading && (
          <>
            <Divider sx={DIVIDER_SX} />

            <SecuritySection formik={formik} passwordRequirements={metadata?.passwordRequirements} />
          </>
        )}

        <Divider sx={DIVIDER_SX} />

        <ServiceAddressDisplay serviceAddress={serviceAddress} onEditServiceAddress={onEditServiceAddress} />

        <Divider sx={DIVIDER_SX} />

        <BillingAddressSection formik={formik} />

        <Divider sx={DIVIDER_SX} />

        <BillDeliverySection formik={formik} />

        <Stack alignSelf="flex-start">
          <LoadingButton variant="contained" type="submit" loading={isSaving} disabled={isTenantConfigLoading}>
            Save and Continue
          </LoadingButton>
        </Stack>
      </Stack>
    </form>
  );
};
